import { gql } from "@apollo/client";
import type { ApolloCache } from "@apollo/client";

const PAGE_FIELDS = `
  id
  tenantId
  slug
  title
  description
  ogImageUrl
  publishedContent
  draftContent
  seoConfig
  isHome
  showInNav
  attribution
  status
  createdAt
  updatedAt
`;

export const CREATE_PAGE = gql`
  mutation CreatePage(
    $slug: String!
    $title: String
    $description: String
    $ogImageUrl: String
    $draftContent: String
    $isHome: Boolean
    $showInNav: Boolean
  ) {
    createPage(
      slug: $slug
      title: $title
      description: $description
      ogImageUrl: $ogImageUrl
      draftContent: $draftContent
      isHome: $isHome
      showInNav: $showInNav
    ) {
      ${PAGE_FIELDS}
    }
  }
`;

export const UPDATE_PAGE = gql`
  mutation UpdatePage(
    $id: String!
    $slug: String
    $title: String
    $description: String
    $ogImageUrl: String
    $draftContent: String
    $isHome: Boolean
    $showInNav: Boolean
    $status: String
    $attribution: String
  ) {
    updatePage(
      id: $id
      slug: $slug
      title: $title
      description: $description
      ogImageUrl: $ogImageUrl
      draftContent: $draftContent
      isHome: $isHome
      showInNav: $showInNav
      status: $status
      attribution: $attribution
    ) {
      ${PAGE_FIELDS}
    }
  }
`;

export const DELETE_PAGE = gql`
  mutation DeletePage($id: String!) {
    deletePage(id: $id) {
      id
    }
  }
`;

export const PUBLISH_PAGE = gql`
  mutation PublishPage($id: String!) {
    publishPage(id: $id) {
      ${PAGE_FIELDS}
    }
  }
`;

export const REVERT_PAGE_DRAFT = gql`
  mutation RevertPageDraft($id: String!) {
    revertPageDraft(id: $id) {
      ${PAGE_FIELDS}
    }
  }
`;

export const MINT_PREVIEW_TOKEN = gql`
  mutation MintPreviewToken($id: String!) {
    mintPreviewToken(id: $id) {
      id
      previewToken
      previewTokenExpiresAt
    }
  }
`;

export const REGENERATE_SEO = gql`
  mutation RegenerateSeo($id: String!) {
    regenerateSeo(id: $id) {
      ${PAGE_FIELDS}
    }
  }
`;

export const UPDATE_SEO_CONFIG = gql`
  mutation UpdateSeoConfig($id: String!, $seoConfig: String!) {
    updateSeoConfig(id: $id, seoConfig: $seoConfig) {
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_PAGE_VERSIONS = gql`
  query GetPageVersions(
    $filter: PageVersionFilter
    $page: Int
    $perPage: Int
  ) {
    allPageVersions(
      filter: $filter
      page: $page
      perPage: $perPage
      sortField: "version"
      sortOrder: "DESC"
    ) {
      id
      pageId
      version
      content
      createdBy
      createdByEmail
      createdAt
    }
  }
`;

export const ROLLBACK_PAGE_TO_VERSION = gql`
  mutation RollbackPageToVersion($pageId: String!, $versionId: String!) {
    rollbackPageToVersion(pageId: $pageId, versionId: $versionId) {
      ${PAGE_FIELDS}
    }
  }
`;

/**
 * Evict cached page listings from Apollo's cache.
 * The listing is unmounted while create/edit pages are open,
 * so refetchQueries by name won't refresh them.
 */
export function evictPageListings(cache: ApolloCache<unknown>) {
  cache.evict({ fieldName: "allPages" });
  cache.evict({ fieldName: "_allPagesMeta" });
  cache.gc();
}
